import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@bitsentry-ce/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@bitsentry/components/ui/card";
import { useTranslation } from "@bitsentry-ce/i18n";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback !== undefined && this.props.fallback !== null) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <DefaultErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

interface DefaultErrorFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
  onReload: () => void;
}

function DefaultErrorFallback({
  error,
  errorInfo,
  onReset,
  onReload,
}: DefaultErrorFallbackProps) {
  const { t } = useTranslation();
  const isDevelopment =
    (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process?.env
      ?.NODE_ENV === "development";

  let componentStack: React.ReactNode = null;
  if (errorInfo !== null) {
    componentStack = (
      <pre className="mt-2 text-xs overflow-auto max-h-40">
        {errorInfo.componentStack}
      </pre>
    );
  }

  let errorDetails: React.ReactNode = null;
  if (isDevelopment && error !== null) {
    errorDetails = (
      <div className="space-y-2">
        <div className="text-sm font-medium">
          {t("common.errorBoundary.errorDetails")}
        </div>
        <div className="p-3 bg-destructive/10 rounded-md">
          <p className="text-sm font-mono text-destructive">
            {error.toString()}
          </p>
          {componentStack}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <CardTitle>{t("errors.boundary.somethingWentWrong")}</CardTitle>
          </div>
          <CardDescription>
            {t("errors.boundary.unexpectedError")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorDetails}
          <div className="flex gap-2">
            <Button onClick={onReset} variant="outline" className="flex-1">
              {t("errors.boundary.tryAgain")}
            </Button>
            <Button onClick={onReload} className="flex-1">
              <RefreshCw className="w-4 h-4 mr-2" />
              {t("common.errorBoundary.reloadPage")}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ErrorBoundary;
